'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface EstimatorResult {
  totalWeeks: string;
  phases: {
    name: string;
    duration: string;
  }[];
  recommendation: string;
}

const TimelineEstimator = () => {
  const [companySize, setCompanySize] = useState<string>('');
  const [complexity, setComplexity] = useState<string>('');
  const [result, setResult] = useState<EstimatorResult | null>(null);

  const calculateTimeline = () => {
    if (!companySize || !complexity) return;

    let baseWeeks = 0;
    let phases: { name: string; duration: string }[] = [];
    let recommendation = '';

    // Calculate based on company size and complexity
    if (companySize === 'small' && complexity === 'low') {
      baseWeeks = 8;
      phases = [
        { name: 'Discovery & Strategy', duration: '1 week' },
        { name: 'System Design', duration: '1-2 weeks' },
        { name: 'Development', duration: '3 weeks' },
        { name: 'Testing & Optimization', duration: '1 week' },
        { name: 'Launch & Training', duration: '1 week' },
        { name: 'Ongoing Support', duration: 'Continuous' }
      ];
      recommendation = 'Perfect for focused automation projects with clear scope and immediate ROI potential.';
    } else if (companySize === 'small' && complexity === 'medium') {
      baseWeeks = 12;
      phases = [
        { name: 'Discovery & Strategy', duration: '1-2 weeks' },
        { name: 'System Design', duration: '2 weeks' },
        { name: 'Development', duration: '4-5 weeks' },
        { name: 'Testing & Optimization', duration: '1-2 weeks' },
        { name: 'Launch & Training', duration: '1 week' },
        { name: 'Ongoing Support', duration: 'Continuous' }
      ];
      recommendation = 'Ideal for multi-department automation with integrated workflows and data synchronization.';
    } else if (companySize === 'medium' && complexity === 'medium') {
      baseWeeks = 16;
      phases = [
        { name: 'Discovery & Strategy', duration: '2 weeks' },
        { name: 'System Design', duration: '2-3 weeks' },
        { name: 'Development', duration: '5-6 weeks' },
        { name: 'Testing & Optimization', duration: '2 weeks' },
        { name: 'Launch & Training', duration: '1 week' },
        { name: 'Ongoing Support', duration: 'Continuous' }
      ];
      recommendation = 'Comprehensive transformation with multiple touchpoints and stakeholder coordination.';
    } else if (companySize === 'large' || complexity === 'high') {
      baseWeeks = 20;
      phases = [
        { name: 'Discovery & Strategy', duration: '2-3 weeks' },
        { name: 'System Design', duration: '3-4 weeks' },
        { name: 'Development', duration: '6-8 weeks' },
        { name: 'Testing & Optimization', duration: '2-3 weeks' },
        { name: 'Launch & Training', duration: '1-2 weeks' },
        { name: 'Ongoing Support', duration: 'Continuous' }
      ];
      recommendation = 'Enterprise-scale implementation with complex integrations and extensive change management.';
    } else {
      baseWeeks = 10;
      phases = [
        { name: 'Discovery & Strategy', duration: '1-2 weeks' },
        { name: 'System Design', duration: '2 weeks' },
        { name: 'Development', duration: '3-4 weeks' },
        { name: 'Testing & Optimization', duration: '1-2 weeks' },
        { name: 'Launch & Training', duration: '1 week' },
        { name: 'Ongoing Support', duration: 'Continuous' }
      ];
      recommendation = 'Balanced approach with room for customization and iterative refinement.';
    }

    setResult({
      totalWeeks: `${baseWeeks}-${baseWeeks + 4}`,
      phases,
      recommendation
    });
  };

  return (
    <div className="w-full bg-card rounded-xl border border-border shadow-soft p-8">
      <div className="flex items-start space-x-4 mb-6">
        <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center">
          <Icon name="ClockIcon" size={24} className="text-accent" />
        </div>
        <div>
          <h3 className="text-2xl font-heading font-bold text-foreground mb-2">
            Project Timeline Estimator
          </h3>
          <p className="text-sm text-foreground/80">
            Get a customized timeline estimate based on your business size and project complexity.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="block text-sm font-heading font-semibold text-foreground mb-3">
            Company Size
          </label>
          <div className="space-y-2">
            {[
              { value: 'small', label: 'Small (1-50 employees)' },
              { value: 'medium', label: 'Medium (51-200 employees)' },
              { value: 'large', label: 'Large (200+ employees)' }
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => setCompanySize(option.value)}
                className={`w-full px-4 py-3 rounded-lg border text-left transition-all duration-300 ${
                  companySize === option.value
                    ? 'border-accent bg-accent/10 text-foreground'
                    : 'border-border bg-background text-foreground/80 hover:border-accent/50'
                }`}
              >
                <span className="text-sm font-sans font-semibold">{option.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-heading font-semibold text-foreground mb-3">
            Project Complexity
          </label>
          <div className="space-y-2">
            {[
              { value: 'low', label: 'Low - Single department focus' },
              { value: 'medium', label: 'Medium - Multi-department integration' },
              { value: 'high', label: 'High - Enterprise-wide transformation' }
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => setComplexity(option.value)}
                className={`w-full px-4 py-3 rounded-lg border text-left transition-all duration-300 ${
                  complexity === option.value
                    ? 'border-accent bg-accent/10 text-foreground'
                    : 'border-border bg-background text-foreground/80 hover:border-accent/50'
                }`}
              >
                <span className="text-sm font-sans font-semibold">{option.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <button
        onClick={calculateTimeline}
        disabled={!companySize || !complexity}
        className="w-full px-6 py-3 bg-accent text-accent-foreground font-heading font-semibold rounded-lg hover:bg-accent/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Calculate Timeline
      </button>

      {result && (
        <div className="mt-8 p-6 bg-muted rounded-lg animate-slide-in">
          <div className="flex items-center space-x-3 mb-4">
            <Icon name="CalendarIcon" size={24} className="text-accent" />
            <div>
              <h4 className="text-lg font-heading font-bold text-foreground">
                Estimated Timeline
              </h4>
              <p className="text-2xl font-heading font-bold text-accent">
                {result.totalWeeks} weeks
              </p>
            </div>
          </div>

          <p className="text-sm text-foreground/80 mb-4 leading-relaxed">
            {result.recommendation}
          </p>

          <div className="space-y-3">
            <h5 className="text-sm font-heading font-semibold text-foreground">
              Phase Breakdown:
            </h5>
            {result.phases.map((phase, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <span className="text-sm text-foreground/80">{phase.name}</span>
                <span className="text-sm font-sans font-semibold text-accent">
                  {phase.duration}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimelineEstimator;